/**
 * Geocodificador basado en Wikidata SPARQL
 * 
 * Wikidata es una fuente autoritativa para infraestructuras con entrada
 * enciclopédica que no aparecen en fuentes oficiales ni en OSM.
 * 
 * CRÍTICO para localizar:
 * - Patrimonio cultural (ermitas, castillos, monumentos BIC)
 * - Embalses y presas con coordenadas oficiales
 * - Centrales eléctricas y subestaciones
 * - Infraestructuras históricas con presencia Wikidata
 * 
 * API:
 * - Endpoint SPARQL: https://query.wikidata.org/sparql
 * - Rate limit: ~60 req/min (User-Agent obligatorio)
 * - Coordenadas en propiedad P625 (WGS84)
 * 
 * @module services/geocoding/specialized
 */

import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { FastFuzzy } from '../../../lib/fuzzySearch';
import type { GeocodingResult } from '../../../types/infrastructure';
import { InfrastructureType } from '../../../types/infrastructure';
import proj4 from 'proj4';

/**
 * Elemento Wikidata parseado
 */
export interface WikidataElement {
  /** QID (ej: Q12345) */
  id: string;
  /** Nombre/label en español */
  name: string;
  /** Latitud WGS84 */
  lat: number;
  /** Longitud WGS84 */
  lon: number;
  /** Descripción Wikidata */
  description: string;
  /** Clases Wikidata (P31 instance of) */
  classes: string[];
}

/**
 * Opciones de búsqueda en Wikidata
 */
export interface WikidataSearchOptions {
  /** Nombre a buscar */
  name: string;
  /** Municipio para filtrar */
  municipality: string;
  /** Provincia (mejora precisión del área) */
  province?: string;
  /** Tipo de infraestructura PTEL */
  infrastructureType?: InfrastructureType;
  /** Radio de búsqueda en km (default: 15) */
  radiusKm?: number;
  /** Límite de resultados SPARQL (default: 20) */
  maxResults?: number;
}

/**
 * Mapeo de tipos PTEL a clases Wikidata (P31 instance of)
 * Cada entrada contiene QIDs de clases relevantes
 * @public Exportado para testing
 */
export const PTEL_TO_WIKIDATA_CLASSES: Partial<Record<InfrastructureType, string[]>> = {
  [InfrastructureType.CULTURAL]: [
    'Q23413',    // castillo
    'Q839954',   // yacimiento arqueológico
    'Q570116',   // ermita
    'Q16560',    // palacio
    'Q751876',   // castrum
    'Q33506',    // museo
    'Q24354',    // teatro (edificio)
    'Q856584',   // biblioteca
  ],
  [InfrastructureType.RELIGIOUS]: [
    'Q16970',    // iglesia (edificio)
    'Q570116',   // ermita
    'Q44613',    // monasterio
    'Q160742',   // abadía
    'Q108325',   // capilla
    'Q32815',    // mezquita
    'Q34627',    // sinagoga
  ],
  [InfrastructureType.HYDRAULIC]: [
    'Q12323',    // embalse
    'Q12323',    // presa (alias)
    'Q131681',   // presa
    'Q1002697',  // depuradora (EDAR)
    'Q2298537',  // estación de bombeo
    'Q575759',   // depósito de agua
  ],
  [InfrastructureType.ENERGY]: [
    'Q159719',   // central eléctrica
    'Q786014',   // subestación eléctrica
    'Q194356',   // parque eólico
    'Q1307214',  // planta solar
    'Q1353981',  // central hidroeléctrica
    'Q849706',   // central térmica
  ],
  [InfrastructureType.HEALTH]: [
    'Q16917',    // hospital
    'Q1774898',  // centro de salud
  ],
  [InfrastructureType.EDUCATION]: [
    'Q3914',     // escuela
    'Q9826',     // instituto de educación secundaria
    'Q3918',     // universidad
  ],
  [InfrastructureType.SPORTS]: [
    'Q483110',   // estadio
    'Q1076486',  // polideportivo
    'Q41253',    // cine/teatro
    'Q1076486',  // centro deportivo
  ],
  [InfrastructureType.PORT]: [
    'Q44782',    // puerto
    'Q283202',   // puerto deportivo
    'Q3001412',  // puerto pesquero
  ],
};

/**
 * Geocodificador Wikidata SPARQL para infraestructuras PTEL
 * 
 * Consulta Wikidata para localizar infraestructuras con entrada
 * enciclopédica que no aparecen en otras fuentes de la cascada.
 * 
 * @example
 * ```ts
 * const geocoder = new WikidataGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Embalse de los Bermejales',
 *   municipality: 'Arenas del Rey',
 *   infrastructureType: InfrastructureType.HYDRAULIC
 * });
 * ```
 */
export class WikidataGeocoder {
  private axiosInstance: AxiosInstance;

  private static readonly WGS84 = 'EPSG:4326';
  private static readonly UTM30 = 'EPSG:25830';
  private static readonly SPARQL_URL = 'https://query.wikidata.org/sparql';

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 15000,
      headers: {
        'Accept': 'application/sparql-results+json',
      },
    });

    // Configurar proyección UTM30
    if (!proj4.defs(WikidataGeocoder.UTM30)) {
      proj4.defs(WikidataGeocoder.UTM30, '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
    }
  }

  /**
   * Geocodifica una infraestructura usando Wikidata SPARQL + fuzzy matching
   */
  public async geocode(options: WikidataSearchOptions): Promise<GeocodingResult | null> {
    try {
      const sparql = this.buildSPARQL(options);
      const elements = await this.executeSPARQL(sparql);

      if (elements.length === 0) {
        return null;
      }

      const bestMatch = this.findBestMatch(options.name, elements);
      if (!bestMatch) {
        return null;
      }

      // WGS84 → UTM30
      const [x, y] = proj4(WikidataGeocoder.WGS84, WikidataGeocoder.UTM30, [bestMatch.element.lon, bestMatch.element.lat]);

      return {
        x,
        y,
        confidence: Math.round(bestMatch.score * 100),
        source: 'Wikidata',
        sourceLayer: 'SPARQL',
        matchedName: bestMatch.element.name,
        fuzzyScore: bestMatch.score,
        municipality: options.municipality,
        province: options.province || '',
        metadata: {
          wikidataId: bestMatch.element.id,
          description: bestMatch.element.description,
          classes: bestMatch.element.classes,
        },
      };
    } catch (error) {
      console.error('Error geocodificando vía Wikidata:', error);
      return null;
    }
  }

  /**
   * Construye query SPARQL para buscar infraestructuras en un municipio
   * 
   * Estrategia: busca entidades con P625 (coordenadas) en un municipio
   * de Andalucía, filtrando por clases Wikidata según tipología PTEL.
   */
  public buildSPARQL(options: WikidataSearchOptions): string {
    const limit = options.maxResults ?? 20;
    const _searchTerm = options.name.replace(/'/g, "\\'");
    const municipality = options.municipality.replace(/'/g, "\\'");

    // Filtro de clases Wikidata si hay tipo PTEL
    const classFilter = this.buildClassFilter(options.infrastructureType);

    // Buscar en el municipio andaluz con coordenadas
    return `SELECT ?item ?itemLabel ?itemDescription ?coord ?classLabel WHERE {
  ?item wdt:P625 ?coord .
  ${classFilter}
  ?item wdt:P131* ?muni .
  ?muni rdfs:label "${municipality}"@es .
  ?muni wdt:P31 wd:Q2074737 .
  OPTIONAL { ?item wdt:P31 ?class . }
  SERVICE wikibase:label { bd:serviceParam wikibase:language "es,en" . }
}
LIMIT ${limit}`;
  }

  /**
   * Construye filtro SPARQL por clases Wikidata
   */
  private buildClassFilter(type?: InfrastructureType): string {
    if (!type) return '';
    const classes = PTEL_TO_WIKIDATA_CLASSES[type];
    if (!classes || classes.length === 0) return '';

    // Eliminar duplicados
    const unique = [...new Set(classes)];
    const values = unique.map(q => `wd:${q}`).join(' ');
    return `?item wdt:P31/wdt:P279* ?targetClass .\n  VALUES ?targetClass { ${values} }`;
  }

  /**
   * Ejecuta query SPARQL contra Wikidata
   */
  public async executeSPARQL(sparql: string): Promise<WikidataElement[]> {
    const response = await this.axiosInstance.get(WikidataGeocoder.SPARQL_URL, {
      params: { query: sparql, format: 'json' },
    });

    const bindings = response.data?.results?.bindings;
    if (!Array.isArray(bindings) || bindings.length === 0) {
      return [];
    }

    return this.parseBindings(bindings);
  }

  /**
   * Parsea bindings SPARQL a WikidataElement[]
   * Agrupa por QID (una entidad puede tener múltiples clases)
   */
  private parseBindings(bindings: Record<string, { value: string; type: string }>[]): WikidataElement[] {
    const map = new Map<string, WikidataElement>();

    for (const b of bindings) {
      const uri = b.item?.value || '';
      const id = uri.split('/').pop() || '';
      if (!id || !b.coord?.value) continue;

      // Parsear Point(lon lat) de WKT
      const coords = this.parseWKTPoint(b.coord.value);
      if (!coords) continue;

      const existing = map.get(id);
      if (existing) {
        // Agregar clase adicional
        const cls = b.classLabel?.value;
        if (cls && !existing.classes.includes(cls)) {
          existing.classes.push(cls);
        }
      } else {
        map.set(id, {
          id,
          name: b.itemLabel?.value || id,
          lat: coords.lat,
          lon: coords.lon,
          description: b.itemDescription?.value || '',
          classes: b.classLabel?.value ? [b.classLabel.value] : [],
        });
      }
    }

    return Array.from(map.values());
  }

  /**
   * Parsea WKT Point a coordenadas
   * Formato: "Point(lon lat)" (ojo: lon primero en WKT)
   */
  private parseWKTPoint(wkt: string): { lat: number; lon: number } | null {
    const match = wkt.match(/Point\(([^\s]+)\s+([^\s)]+)\)/i);
    if (!match) return null;
    const lon = parseFloat(match[1]);
    const lat = parseFloat(match[2]);
    if (isNaN(lon) || isNaN(lat)) return null;
    return { lat, lon };
  }

  /**
   * Encuentra el mejor match usando fuzzy matching
   */
  private findBestMatch(
    searchName: string,
    elements: WikidataElement[]
  ): { element: WikidataElement; score: number } | null {
    if (elements.length === 0) return null;

    const fuzzy = new FastFuzzy(elements as unknown as Record<string, unknown>[], {
      keys: ['name'],
      threshold: 0.5,
    });

    const results = fuzzy.search(searchName);

    if (results.length === 0) {
      // Si pocos resultados, retornar primero con confianza baja
      if (elements.length <= 3) {
        return { element: elements[0], score: 0.35 };
      }
      return null;
    }

    const best = results[0];
    return {
      element: best.item as unknown as WikidataElement,
      score: 1 - (best.score || 1),
    };
  }

  /**
   * Convierte coordenadas WGS84 a UTM30
   */
  public toUTM30(lon: number, lat: number): { x: number; y: number } {
    const [x, y] = proj4(WikidataGeocoder.WGS84, WikidataGeocoder.UTM30, [lon, lat]);
    return { x, y };
  }

  /**
   * Convierte coordenadas UTM30 a WGS84
   */
  public toWGS84(x: number, y: number): { lon: number; lat: number } {
    const [lon, lat] = proj4(WikidataGeocoder.UTM30, WikidataGeocoder.WGS84, [x, y]);
    return { lon, lat };
  }

  /**
   * Obtiene las clases Wikidata para un tipo PTEL
   */
  public getClassesForType(type: InfrastructureType): string[] {
    return PTEL_TO_WIKIDATA_CLASSES[type] || [];
  }
}
